import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Flex,
  FormLabel,
  InputGroup,
  ListItem,
  Spacer,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  UnorderedList,
} from '@chakra-ui/react';
import { ErrorMessage, FormikProps, getIn } from 'formik';
import _ from 'lodash';
import React, { useCallback, useState } from 'react';

import {
  checkWaiveConfigErrors,
  getAvailableFieldsWithTypes,
} from '@finley-technologies/calc-agent';
import { CreditAgreement } from '@finley-technologies/finley-types';

import { LoanProductGQLType } from 'types/dashboard-core-gqlTypes.generated';

import { WipConfig } from '../../types';
import { ConcentrationLimitConfigConditionalProperties } from './ConcentrationLimitConfigConditionalProperties';
import { colors } from 'packages/common-frontend/colors';
import { useDraggableRow } from 'packages/common-frontend/components/DragAndDropTable/hooks';
import * as DragAndDropTableTypes from 'packages/common-frontend/components/DragAndDropTable/types';
import { TrDraggableHandle } from 'packages/common-frontend/components/DragAndDropTable/ui';
import { InputFormField } from 'packages/common-frontend/components/FormField';
import { Icon as FinleyIcon } from 'packages/common-frontend/components/icons';
import { MultiSelect } from 'packages/common-frontend/components/MultiSelect';
import {
  getBooleanSymbol,
  getIterationKey,
} from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { DeleteArrayPropertyButton } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/delete/DeleteArrayPropertyButton';
import { EditButton } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/edit/EditButton';
import { EditPropertyModal } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/edit/EditPropertyModal';

import './concentrationLimitConfig.scss';

const headers: (keyof CreditAgreement.DomainEntity.MultiLoanProductConcentrationLimit)[] = [
  'name',
  'criteriaNumber',
  'section',
  'loanProducts',
  '_note_',
  `_config_`,
];

export const createMultiLoanProductConcentrationLimitPropertyShell = (
  entity: Omit<LoanProductGQLType, '_typename'>,
) => ({
  name: 'Placeholder',
  type: CreditAgreement.DomainEntity.PropertyType.ConcentrationLimitProperty,
  value: 'Placeholder',
  criteriaNumber: 'Placeholder criteria number',
  loanProducts: [{ entity }],
  _isWrittenIn_: true,
  _config_: {},
});

export const createConcentrationLimitConfigShell = (
  name?: string,
): CreditAgreement.Config.ConcentrationLimit.SourceConfig => ({
  propertyKey: name ? _.camelCase(name) : 'placeholder',
  calculationConfig: {
    type: 'standard',
    config: {
      filters: [],
    },
  },
  valueConfig: {
    type: 'formula',
    limit: '1',
    valueType: 'number',
  },
  waiveConfigs: [],
});

type MultiLoanProductConcentrationLimitProps = {
  property: CreditAgreement.DomainEntity.MultiLoanProductConcentrationLimitWithEntities;
  basePropertyKey: string;
  idx: number;
  wipConfig?: WipConfig;
  id: string;
  moveRow: DragAndDropTableTypes.MoveRow;
  isDragDisabled: boolean;
  loanProductOptions: { value: string; label: string }[];
  initialSelectedLoanProduct: LoanProductGQLType;
  loanProducts: CreditAgreement.DomainEntity.LoanProductMechanicsWithEntities[];
  currentCommittedConcentrationLimitKeys: string[];
};
const MultiLoanProductConcentrationLimit = ({
  isDragDisabled,
  id,
  moveRow,
  basePropertyKey,
  idx,
  property,
  wipConfig,
  loanProductOptions,
  initialSelectedLoanProduct,
  loanProducts,
  currentCommittedConcentrationLimitKeys,
}: MultiLoanProductConcentrationLimitProps) => {
  const [isEditOpen, setIsEditOpen] = useState<boolean>(false);
  const [
    selectedLoanProductOptions,
    updateSelectedLoanProductOptions,
  ] = useState<string[]>(
    wipConfig && initialSelectedLoanProduct
      ? [initialSelectedLoanProduct.id]
      : [],
  );
  const [
    loanProductMultiSelectTitleText,
    setLoanProductMultiSelectTitleText,
  ] = useState<string>(
    wipConfig && initialSelectedLoanProduct
      ? initialSelectedLoanProduct.name
      : '',
  );

  const [editorErrors, setEditorErrors] = useState<string[]>([]);

  const configPrefix = '_config_';
  const concentrationLimitConfigPrefix = 'concentrationLimitConfig';
  const fieldValuePrefix = `${configPrefix}.${concentrationLimitConfigPrefix}`;

  const concentrationLimitKey = `${basePropertyKey}[${idx}]._config_.concentrationLimitConfig`;

  const validateConditionalProperties = useCallback(
    (values: CreditAgreement.DomainEntity.ConcentrationLimitProperty) => {
      if (editorErrors) {
        return editorErrors;
      }
      return values._config_.concentrationLimitConfig
        ? _.flatMap(
            checkWaiveConfigErrors({
              waiveConfigs:
                values._config_.concentrationLimitConfig.waiveConfigs,
            }),
            (waiveConfig) => waiveConfig.errorMessages,
          )
        : [];
    },
    [editorErrors],
  );

  const onAddConditionalProperties = useCallback(
    (localFormProps: FormikProps<any>) => {
      const currentName = localFormProps.values.name;

      localFormProps.setFieldValue(configPrefix, {
        concentrationLimitConfig: createConcentrationLimitConfigShell(
          currentName,
        ),
      });
    },
    [configPrefix],
  );

  const onDeleteConditionalProperties = useCallback(
    (localFormProps: FormikProps<any>) => {
      localFormProps.setFieldValue(configPrefix, {});
    },
    [configPrefix],
  );

  const getIsPropertyKeyCurrentlyCommitted = useCallback(
    (localFormProps: FormikProps<any>) => {
      if (
        localFormProps?.values[configPrefix]?.[concentrationLimitConfigPrefix]
          ?.propertyKey
      ) {
        return _.includes(
          currentCommittedConcentrationLimitKeys,
          localFormProps.values[configPrefix][concentrationLimitConfigPrefix]
            .propertyKey,
        );
      }
      return false;
    },
    [currentCommittedConcentrationLimitKeys],
  );

  const availableFields = getAvailableFieldsWithTypes({
    loanProducts,
    loanProductIds: selectedLoanProductOptions,
  });

  const ConditionalPropertiesComponent = useCallback(
    (localFormProps: FormikProps<any>, validationErrors: Array<string>) => {
      return (
        <ConcentrationLimitConfigConditionalProperties
          localFormProps={localFormProps}
          validationErrors={validationErrors}
          wipConfig={wipConfig}
          concentrationLimitKey={concentrationLimitKey}
          configPrefix={configPrefix}
          concentrationLimitConfigPrefix={concentrationLimitConfigPrefix}
          fieldValuePrefix={fieldValuePrefix}
          setEditorErrors={setEditorErrors}
          availableFields={availableFields}
        />
      );
    },
    [fieldValuePrefix, wipConfig, concentrationLimitKey, availableFields],
  );

  const multiSelectOnChange = (newValue: string) => {
    const index = selectedLoanProductOptions.indexOf(newValue);
    const newLoanProductOptions = selectedLoanProductOptions;
    if (index >= 0) {
      newLoanProductOptions.splice(index, 1);
      updateSelectedLoanProductOptions(newLoanProductOptions);
    } else {
      newLoanProductOptions.push(newValue);
    }

    if (newLoanProductOptions.length > 0) {
      const newLoanProductOptionsLabels = _.map(
        newLoanProductOptions,
        (loanProductId) => {
          const loanProduct = _.find(
            loanProductOptions,
            ({ value }) => loanProductId === value,
          );
          return loanProduct ? loanProduct.label : '';
        },
      );
      setLoanProductMultiSelectTitleText(
        newLoanProductOptionsLabels.join(', '),
      );
    } else {
      setLoanProductMultiSelectTitleText('Select loan products...');
    }
    return newLoanProductOptions;
  };

  const fullPropertyKey = `${basePropertyKey}[${idx}]`;

  return (
    <TrDraggableHandle
      id={id}
      acceptedType="concentrationLimitProperty"
      index={idx}
      moveRow={moveRow}
      isDragDisabled={isDragDisabled}
    >
      <>
        {_.map(headers, (header) => {
          if (header === '_config_') {
            return (
              <Td key={`${basePropertyKey}[${id}].${header}`}>
                {getBooleanSymbol(
                  Boolean(property._config_?.concentrationLimitConfig),
                )}
              </Td>
            );
          }
          if (header === 'loanProducts') {
            // eslint-disable-next-line @typescript-eslint/no-shadow
            const loanProducts = property[header];
            return (
              <Td key={`${basePropertyKey}[${id}].${header}`}>
                {loanProducts.length ? (
                  <UnorderedList>
                    {_.map(loanProducts, (loanProduct) => {
                      const label =
                        _.find(
                          loanProductOptions,
                          ({ value }) => value === loanProduct.entity.id,
                        )?.label || '';
                      return (
                        <ListItem
                          key={`loan-product-${id}-${loanProduct.entity.id}`}
                        >
                          {label}
                        </ListItem>
                      );
                    })}
                  </UnorderedList>
                ) : (
                  '-'
                )}
              </Td>
            );
          }
          return (
            <Td key={`${basePropertyKey}.concentrationLimits[${id}].${header}`}>
              {property[header] ? property[header] : '-'}
            </Td>
          );
        })}
        {wipConfig?.isMutateAllowed(fullPropertyKey) && isDragDisabled && (
          <Td>
            <EditButton setIsOpen={setIsEditOpen} />
            <EditPropertyModal
              isOpen={isEditOpen}
              setIsOpen={setIsEditOpen}
              wipConfig={wipConfig}
              basePropertyKey={fullPropertyKey}
              modalHeaderLabel="Multi Loan Product Concentration Limit"
              conditionalProperties={{
                conditionalPropertiesComponent: ConditionalPropertiesComponent,
                conditionalPropertiesToggleLabel: 'Configuration',
                conditionalPropertiesValidation: validateConditionalProperties,
                useConditionalProperties: !!property._config_
                  ?.concentrationLimitConfig,
                onDeleteConditionalProperties: (
                  localFormProps: FormikProps<any>,
                ) => onDeleteConditionalProperties(localFormProps),
                onAddConditionalProperties: (
                  localFormProps: FormikProps<any>,
                ) =>
                  wipConfig?.isMutateAllowed(
                    `${basePropertyKey}[${idx}]._config_`,
                  ) && onAddConditionalProperties(localFormProps),
                getIsPropertyKeyCurrentlyCommitted: (
                  localFormProps: FormikProps<any>,
                ) => getIsPropertyKeyCurrentlyCommitted(localFormProps),
              }}
            >
              {(localFormProps) => (
                <>
                  <InputFormField
                    propName="value"
                    label="Value"
                    formProps={localFormProps}
                    value={localFormProps.values.value}
                    placeholder="Enter value"
                  />
                  <InputFormField
                    propName="criteriaNumber"
                    label="Criteria Number"
                    formProps={localFormProps}
                    value={localFormProps.values.criteriaNumber}
                    placeholder="Enter value"
                  />
                  <Box width="100%">
                    <FormLabel mb="8px">Loan Products</FormLabel>
                    <InputGroup width="100%">
                      <Box w="sm">
                        <MultiSelect
                          options={loanProductOptions}
                          onChange={(value: string) => {
                            const selectedLoanProducts = multiSelectOnChange(
                              value,
                            );
                            const newLoanProducts = _.map(
                              selectedLoanProducts,
                              (loanProduct) => {
                                return {
                                  entity:
                                    wipConfig.loanProductsMap[loanProduct],
                                };
                              },
                            );
                            localFormProps.setFieldValue(
                              'loanProducts',
                              newLoanProducts,
                              true,
                            );
                            localFormProps.setFieldTouched(
                              'loanProducts',
                              true,
                            );
                          }}
                          selectedOptions={selectedLoanProductOptions}
                          titleText={loanProductMultiSelectTitleText}
                        />
                      </Box>
                    </InputGroup>
                    <ErrorMessage name="loanProducts">
                      {(msg) => (
                        <Text color={colors.error} mt="5px">
                          {msg}
                        </Text>
                      )}
                    </ErrorMessage>
                  </Box>
                </>
              )}
            </EditPropertyModal>
            <DeleteArrayPropertyButton
              wipConfig={wipConfig}
              basePropertyKey={basePropertyKey}
              idx={idx}
            />
          </Td>
        )}
      </>
    </TrDraggableHandle>
  );
};

type MultiLoanProductConcentrationLimitsProps = {
  multiLoanProductConcentrationLimits: CreditAgreement.DomainEntity.MultiLoanProductConcentrationLimitWithEntities[];
  basePropertyKey: string;
  loanProductOptions: { value: string; label: string }[];
  wipConfig?: WipConfig;
  loanProducts: CreditAgreement.DomainEntity.LoanProductMechanicsWithEntities[];
  currentCommittedConcentrationLimitKeys: string[];
};
export const MultiLoanProductConcentrationLimits = ({
  multiLoanProductConcentrationLimits,
  basePropertyKey,
  loanProductOptions,
  wipConfig,
  loanProducts: lpMechanics,
  currentCommittedConcentrationLimitKeys,
}: MultiLoanProductConcentrationLimitsProps) => {
  const {
    moveRow,
    useRowState,
    useOrderLockedState,
    ui: { LockOrderButton, RestoreOrderButton, SaveOrderCallout },
  } = useDraggableRow<CreditAgreement.DomainEntity.MultiLoanProductConcentrationLimitWithEntities>(
    {
      comparisonKey: 'name',
    },
  );

  const [multiLoanProductConcentrationLimitsState] = useRowState(
    multiLoanProductConcentrationLimits,
  );
  const [isOrderLocked] = useOrderLockedState();

  const handleAdd = (singleLoanProduct: LoanProductGQLType) => {
    const propertyKey = basePropertyKey;
    const newMultiLoanProductConcentrationLimit = createMultiLoanProductConcentrationLimitPropertyShell(
      singleLoanProduct,
    );
    const previousMultiLoanProductConcentrationLimits = getIn(
      wipConfig?.formProps.values,
      propertyKey,
    );
    wipConfig?.formProps.setFieldValue(propertyKey, [
      ...previousMultiLoanProductConcentrationLimits,
      newMultiLoanProductConcentrationLimit,
    ]);
  };

  const onSaveOrder = useCallback(() => {
    wipConfig?.formProps.setFieldValue(
      'borrowingBaseMechanics.calcAgentMechanics.multiLoanProductConcentrationLimits',
      multiLoanProductConcentrationLimitsState,
    );
  }, [wipConfig, multiLoanProductConcentrationLimitsState]);

  let singleLoanProduct: LoanProductGQLType;
  if (wipConfig) {
    singleLoanProduct = Object.values(wipConfig.loanProductsMap)[0];
  }

  return (
    <Box mt="20px" mb="35px" width="100%">
      <Flex mb="10px" alignItems="center">
        {wipConfig?.isMutateAllowed(basePropertyKey) && (
          <LockOrderButton onSaveOrder={onSaveOrder} />
        )}
        <Text fontWeight="bold" fontSize="20px">
          Multi-Loan Product Concentration Limits
        </Text>
        {wipConfig?.isMutateAllowed(basePropertyKey) && <RestoreOrderButton />}
        {wipConfig?.isMutateAllowed(basePropertyKey) && (
          <>
            <Spacer />
            <Button
              aria-label="Add Multi Loan Product Concentration Limit"
              minW="fit-content"
              borderWidth="2px"
              colorScheme="v2-aurora"
              variant="secondary"
              onClick={() => handleAdd(singleLoanProduct)}
              isDisabled={loanProductOptions.length < 2 || !isOrderLocked}
            >
              <FinleyIcon icon="plus" />
              <Text ml="16px">Add Multi Loan Product Concentration Limit</Text>
            </Button>
          </>
        )}
      </Flex>
      {wipConfig?.isMutateAllowed(basePropertyKey) && <SaveOrderCallout />}
      {loanProductOptions.length < 2 && (
        <Alert status="info" mb="20px" mt="20px">
          <AlertIcon />
          <AlertDescription>
            Please configure at least two loan products before adding a
            multi-loan product concentration limit.
          </AlertDescription>
        </Alert>
      )}
      {loanProductOptions.length >= 2 &&
        (!_.isEmpty(multiLoanProductConcentrationLimitsState) ? (
          <Table borderWidth="1px">
            <Thead>
              <Tr>
                {wipConfig && !isOrderLocked && <Th />}
                {_.map(headers, (header: string) => (
                  <Th key={`header-${header}`}>{_.startCase(header)}</Th>
                ))}
                {wipConfig && isOrderLocked && <Th />}
              </Tr>
            </Thead>
            <Tbody>
              {_.map(
                multiLoanProductConcentrationLimitsState,
                (concentrationLimit, testIdx) => {
                  const id = getIterationKey(concentrationLimit);
                  return (
                    <MultiLoanProductConcentrationLimit
                      key={id}
                      property={concentrationLimit}
                      wipConfig={wipConfig}
                      basePropertyKey={basePropertyKey}
                      idx={testIdx}
                      id={id}
                      moveRow={moveRow}
                      isDragDisabled={isOrderLocked}
                      loanProductOptions={loanProductOptions}
                      initialSelectedLoanProduct={singleLoanProduct}
                      loanProducts={lpMechanics}
                      currentCommittedConcentrationLimitKeys={
                        currentCommittedConcentrationLimitKeys
                      }
                    />
                  );
                },
              )}
            </Tbody>
          </Table>
        ) : (
          <Text>
            There are no multi-loan product concentration limits currently
            configured.
          </Text>
        ))}
    </Box>
  );
};
